import React, { useState, useRef, useEffect, useCallback } from 'react';
import { apiFetch, useObjectUrl } from '../api';

interface Meta {
    schedule_id: number;
    video_id: number;
    sport_type: string;
    period_order: number;
    fps: number;
    start_frame: number;
    end_frame: number;
    total: number;
}

interface Point { x: number; y: number; }
interface QueueEntry { seg_id: number; points: Point[]; }

// Replicates gen_colors() in scripts/generate_annotation_guide.py so overlay colors
// match the guide image: OpenCV HSV (hue=int(180*i/n), S=200, V=180) -> RGB.
const hsvToRgb = (h: number, s: number, v: number): string => {
    const c = v * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = v - c;
    let r = 0, g = 0, b = 0;
    if (h < 60) { r = c; g = x; }
    else if (h < 120) { r = x; g = c; }
    else if (h < 180) { g = c; b = x; }
    else if (h < 240) { g = x; b = c; }
    else if (h < 300) { r = x; b = c; }
    else { r = c; b = x; }
    return `rgb(${Math.round((r + m) * 255)},${Math.round((g + m) * 255)},${Math.round((b + m) * 255)})`;
};
const segColor = (id: number, n: number) => hsvToRgb((Math.floor((180 * id) / Math.max(n, 1)) * 2) % 360, 200 / 255, 180 / 255);

const KeypointAnnotator: React.FC = () => {
    const [scheduleIdInput, setScheduleIdInput] = useState('');
    const [startInput, setStartInput] = useState('');
    const [endInput, setEndInput] = useState('');

    const [meta, setMeta] = useState<Meta | null>(null);
    const [extracting, setExtracting] = useState(false);
    const [progress, setProgress] = useState<{ done: number; total: number }>({ done: 0, total: 0 });
    const [error, setError] = useState<string | null>(null);
    const [status, setStatus] = useState<string | null>(null);

    const [frameIdx, setFrameIdx] = useState(0);
    const [activeSegId, setActiveSegId] = useState(0);
    const [pending, setPending] = useState<Point[]>([]);
    const [frameData, setFrameData] = useState<Record<number, QueueEntry[]>>({});

    const [segTypes, setSegTypes] = useState<string[]>([]);
    const [segCount, setSegCount] = useState(0);
    const [guidePath, setGuidePath] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [showGuide, setShowGuide] = useState(false);
    const [panelOpen, setPanelOpen] = useState(true);

    const imgRef = useRef<HTMLImageElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const areaRef = useRef<HTMLDivElement>(null);
    const segInputRef = useRef<HTMLInputElement>(null);

    const currentFrame = meta ? meta.start_frame + frameIdx : 0;
    const guideUrl = useObjectUrl(guidePath);
    const frameUrl = useObjectUrl(meta ? `/train/extracted_frame?sport_type=${encodeURIComponent(meta.sport_type)}&schedule_id=${meta.schedule_id}&period_order=${meta.period_order}&frame=${currentFrame}` : null);

    const loadLegend = (sport: string) => {
        setGuidePath(`/train/annotation_guide?sport=${encodeURIComponent(sport)}`);
        apiFetch(`/train/annotation_segments?sport=${encodeURIComponent(sport)}`)
            .then((r) => (r.ok ? r.json() : { count: 0, types: [] }))
            .then((d) => { setSegTypes(d.types || []); setSegCount(d.count || 0); })
            .catch(() => { setSegTypes([]); setSegCount(0); });
    };

    const loadExisting = async (m: Meta) => {
        try {
            const res = await apiFetch(`/train/keypoints?sport_type=${encodeURIComponent(m.sport_type)}&schedule_id=${m.schedule_id}&period_order=${m.period_order}`);
            if (!res.ok) return;
            const ann = (await res.json()).annotations || {};
            const inRange: Record<number, QueueEntry[]> = {};
            for (const k of Object.keys(ann)) {
                const f = Number(k);
                if (f >= m.start_frame && f <= m.end_frame) inRange[f] = ann[k];
            }
            if (Object.keys(inRange).length) setFrameData(inRange);
        } catch { /* no existing annotations */ }
    };

    const extract = async () => {
        const sid = parseInt(scheduleIdInput.trim(), 10);
        const s = parseInt(startInput.trim(), 10);
        const e = parseInt(endInput.trim(), 10);
        if (isNaN(sid) || isNaN(s) || isNaN(e)) { setError('Enter a valid schedule ID and frame range'); return; }
        if (e < s) { setError('End frame must be >= start frame'); return; }

        setMeta(null);
        setFrameData({});
        setPending([]);
        setFrameIdx(0);
        setError(null);
        setStatus(null);
        setExtracting(true);
        setProgress({ done: 0, total: e - s + 1 });

        try {
            const res = await apiFetch('/train/extract_frames', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ schedule_id: sid, start_frame: s, end_frame: e }),
            });
            if (!res.ok || !res.body) throw new Error((await res.text()) || `HTTP ${res.status}`);

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buf = '';
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                const lines = buf.split('\n');
                buf = lines.pop() || '';
                for (const line of lines) {
                    if (!line.trim()) continue;
                    const msg = JSON.parse(line);
                    if (msg.type === 'meta') { setMeta(msg); loadLegend(msg.sport_type); loadExisting(msg); }
                    else if (msg.type === 'progress' || msg.type === 'done') setProgress({ done: msg.done, total: msg.total });
                    else if (msg.type === 'error') throw new Error(msg.detail);
                }
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setExtracting(false);
        }
    };

    const commit = useCallback(() => {
        if (pending.length === 0) return;
        setFrameData((prev) => {
            const entries = prev[currentFrame] ? [...prev[currentFrame]] : [];
            entries.push({ seg_id: activeSegId, points: pending });
            return { ...prev, [currentFrame]: entries };
        });
        setPending([]);
    }, [pending, activeSegId, currentFrame]);

    const stepFrame = useCallback((delta: number) => {
        if (!meta) return;
        const ni = Math.min(Math.max(frameIdx + delta, 0), meta.total - 1);
        if (ni !== frameIdx) { setFrameIdx(ni); setPending([]); }
    }, [meta, frameIdx]);

    const deleteEntry = (i: number) => {
        setFrameData((prev) => {
            const entries = [...(prev[currentFrame] || [])];
            entries.splice(i, 1);
            const next = { ...prev };
            if (entries.length) next[currentFrame] = entries; else delete next[currentFrame];
            return next;
        });
    };

    useEffect(() => {
        const onKey = (ev: KeyboardEvent) => {
            const inInput = (document.activeElement as HTMLElement | null)?.tagName === 'INPUT';
            if (ev.key === 'Tab' && !inInput) { ev.preventDefault(); setShowGuide((s) => !s); }
            else if (ev.key === 'ArrowLeft') { ev.preventDefault(); stepFrame(-1); }
            else if (ev.key === 'ArrowRight') { ev.preventDefault(); stepFrame(1); }
            else if (ev.key === 'Enter' && inInput && document.activeElement === segInputRef.current) segInputRef.current?.blur();
            else if (ev.key === 'Enter' && !inInput) commit();
            else if (ev.key === 'Backspace' && !inInput) { ev.preventDefault(); setPending((p) => p.slice(0, -1)); }
            else if (/^[0-9]$/.test(ev.key) && !inInput) {
                ev.preventDefault();
                setActiveSegId(parseInt(ev.key, 10));
                requestAnimationFrame(() => segInputRef.current?.focus());
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [stepFrame, commit]);

    const draw = useCallback(() => {
        const img = imgRef.current, canvas = canvasRef.current, area = areaRef.current;
        if (!img || !canvas || !area) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const cw = area.clientWidth, ch = area.clientHeight;
        canvas.width = cw;
        canvas.height = ch;
        ctx.clearRect(0, 0, cw, ch);
        const nw = img.naturalWidth, nh = img.naturalHeight;
        if (!nw || !nh) return;

        const contA = cw / ch, imgA = nw / nh;
        let dw: number, dh: number, ox: number, oy: number;
        if (contA > imgA) { dh = ch; dw = dh * imgA; ox = (cw - dw) / 2; oy = 0; }
        else { dw = cw; dh = dw / imgA; ox = 0; oy = (ch - dh) / 2; }
        const toC = (p: Point): [number, number] => [ox + (p.x / nw) * dw, oy + (p.y / nh) * dh];

        const drawEntry = (points: Point[], seg: number, active: boolean) => {
            const color = segColor(seg, segCount);
            ctx.strokeStyle = color;
            ctx.fillStyle = color;
            ctx.lineWidth = 2;
            ctx.beginPath();
            points.forEach((p, i) => { const [x, y] = toC(p); if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y); });
            ctx.stroke();
            points.forEach((p) => { const [x, y] = toC(p); ctx.beginPath(); ctx.arc(x, y, active ? 5 : 4, 0, Math.PI * 2); ctx.fill(); });
            if (points.length) {
                const [x, y] = toC(points[0]);
                ctx.font = 'bold 13px sans-serif';
                ctx.fillStyle = color;
                ctx.fillRect(x + 6, y - 16, 18, 16);
                ctx.fillStyle = '#000';
                ctx.fillText(String(seg), x + 9, y - 4);
            }
        };

        (frameData[currentFrame] || []).forEach((entry) => drawEntry(entry.points, entry.seg_id, false));
        if (pending.length) drawEntry(pending, activeSegId, true);
    }, [frameData, currentFrame, pending, activeSegId, segCount]);

    useEffect(() => {
        draw();
        window.addEventListener('resize', draw);
        return () => window.removeEventListener('resize', draw);
    }, [draw]);

    const onCanvasClick = (e: React.MouseEvent) => {
        segInputRef.current?.blur();
        const img = imgRef.current, canvas = canvasRef.current;
        if (!img || !canvas || !img.naturalWidth) return;
        const rect = canvas.getBoundingClientRect();
        const cw = rect.width, ch = rect.height, nw = img.naturalWidth, nh = img.naturalHeight;
        const contA = cw / ch, imgA = nw / nh;
        let dw: number, dh: number, ox: number, oy: number;
        if (contA > imgA) { dh = ch; dw = dh * imgA; ox = (cw - dw) / 2; oy = 0; }
        else { dw = cw; dh = dw / imgA; ox = 0; oy = (ch - dh) / 2; }
        const nx = ((e.clientX - rect.left) - ox) / dw * nw;
        const ny = ((e.clientY - rect.top) - oy) / dh * nh;
        if (nx < 0 || nx > nw || ny < 0 || ny > nh) return;
        setPending((prev) => [...prev, { x: nx, y: ny }]);
    };

    const submit = async () => {
        if (!meta) return;
        const frames = Object.keys(frameData).map(Number).sort((a, b) => a - b);
        if (frames.length === 0) { setError('No annotations to submit'); return; }

        let csv = 'frame,x,y,seg_id\n';
        for (const f of frames)
            for (const entry of frameData[f])
                for (const p of entry.points)
                    csv += `${f},${Math.round(p.x)},${Math.round(p.y)},${entry.seg_id}\n`;

        setSubmitting(true);
        setStatus(null);
        setError(null);
        try {
            const res = await apiFetch('/train/upload_annotation', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    schedule_id: meta.schedule_id,
                    period_order: meta.period_order,
                    sport_type: meta.sport_type,
                    frames,
                    csv,
                }),
            });
            const data = await res.json();
            if (!res.ok || data.error) throw new Error(data.error || data.detail || `HTTP ${res.status}`);
            setStatus(`Uploaded ${data.uploaded_frames} new frames (dataset total: ${data.total_frames}) to ${data.gcs_dir}`);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const annotatedFrames = Object.keys(frameData).length;
    const queue = frameData[currentFrame] || [];
    const ready = meta && !extracting;

    const inputStyle: React.CSSProperties = { padding: '8px', width: '110px' };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Toolbar */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <input type="text" placeholder="Schedule ID" value={scheduleIdInput}
                    onChange={(e) => setScheduleIdInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && extract()} style={inputStyle} />
                <input type="text" placeholder="Start frame" value={startInput}
                    onChange={(e) => setStartInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && extract()} style={inputStyle} />
                <input type="text" placeholder="End frame" value={endInput}
                    onChange={(e) => setEndInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && extract()} style={inputStyle} />
                <button onClick={extract} disabled={extracting}
                    style={{ padding: '8px 16px', backgroundColor: '#2196F3', color: '#fff', border: 'none', cursor: 'pointer' }}>
                    {extracting ? 'Extracting...' : 'Extract Frames'}
                </button>

                {ready && (
                    <>
                        <span style={{ marginLeft: '10px' }}>
                            Segment ID:{' '}
                            <input ref={segInputRef} type="number" value={activeSegId}
                                onChange={(e) => setActiveSegId(parseInt(e.target.value, 10) || 0)}
                                style={{ width: '70px', padding: '6px', borderLeft: `6px solid ${segColor(activeSegId, segCount)}` }} />
                        </span>
                        <span style={{ color: '#666' }}>
                            {segTypes[activeSegId] || '?'} | {meta!.sport_type} | annotated {annotatedFrames} frames
                        </span>
                        <button onClick={submit} disabled={submitting || annotatedFrames === 0}
                            style={{ marginLeft: 'auto', padding: '8px 16px', backgroundColor: '#4CAF50', color: '#fff', border: 'none', cursor: 'pointer' }}>
                            {submitting ? 'Submitting...' : 'Submit'}
                        </button>
                    </>
                )}
            </div>

            {extracting && (
                <div style={{ marginBottom: '10px' }}>
                    <div style={{ height: '10px', background: '#eee', borderRadius: '5px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%`, background: '#2196F3' }} />
                    </div>
                    <span style={{ fontSize: '12px', color: '#666' }}>Extracting frames {progress.done}/{progress.total}</span>
                </div>
            )}

            {error && <div style={{ color: 'red', marginBottom: '8px' }}>Error: {error}</div>}
            {status && <div style={{ color: '#155724', background: '#d4edda', padding: '6px 12px', borderRadius: '4px', marginBottom: '8px' }}>{status}</div>}

            {ready ? (
                <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                    {/* Annotation area */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative' }}>
                        <div ref={areaRef} style={{ flex: 1, position: 'relative', background: '#000', minHeight: 0 }}>
                            <img ref={imgRef}
                                src={frameUrl || undefined}
                                onLoad={draw}
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            <canvas ref={canvasRef} onClick={onCanvasClick}
                                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', cursor: 'crosshair' }} />

                            {/* Guide overlay (Tab) */}
                            {showGuide && guideUrl && (
                                <div onClick={() => setShowGuide(false)}
                                    style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                    <img src={guideUrl} alt="segment guide" style={{ maxWidth: '95%', maxHeight: '95%', objectFit: 'contain' }} />
                                </div>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '6px 8px', background: '#222', color: '#fff' }}>
                            <button onClick={() => stepFrame(-1)} disabled={frameIdx === 0}>← Prev</button>
                            <span>Frame {currentFrame} ({frameIdx + 1}/{meta!.total})</span>
                            <button onClick={() => stepFrame(1)} disabled={frameIdx >= meta!.total - 1}>Next →</button>
                            <span style={{ marginLeft: '20px', color: '#bbb', fontSize: '12px' }}>
                                Type # to set seg · Click to add points · Enter to commit seg {activeSegId} ({pending.length}) · Backspace to undo · ←/→ frame · Tab guide
                            </span>
                        </div>

                        {/* Foldable queue panel (overlays the image) */}
                        <button onClick={() => setPanelOpen((o) => !o)}
                            style={{ position: 'absolute', top: 8, right: 8, zIndex: 2, padding: '4px 10px', background: '#222', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                            {panelOpen ? 'Hide ▸' : '◂ Queue'}
                        </button>
                        {panelOpen && (
                            <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: '300px', overflowY: 'auto', padding: '40px 12px 12px', background: 'rgba(255,255,255,0.95)', borderLeft: '1px solid #ddd', zIndex: 1 }}>
                                <h4 style={{ margin: '4px 0' }}>Queue (frame {currentFrame})</h4>
                                {queue.length === 0 && <div style={{ color: '#999', fontSize: '13px' }}>No committed segments yet</div>}
                                {queue.map((entry, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', borderBottom: '1px solid #eee' }}>
                                        <span style={{ width: '14px', height: '14px', background: segColor(entry.seg_id, segCount), display: 'inline-block', borderRadius: '3px' }} />
                                        <span style={{ flex: 1, fontSize: '13px' }}>
                                            seg {entry.seg_id} ({segTypes[entry.seg_id] || '?'}) — {entry.points.length} pts
                                        </span>
                                        <button onClick={() => deleteEntry(i)} style={{ color: '#dc3545', border: 'none', background: 'none', cursor: 'pointer' }}>✕</button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                !extracting && (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                        Enter a schedule ID and frame range, then click Extract Frames to begin.
                    </div>
                )
            )}
        </div>
    );
};

export default KeypointAnnotator;
